import { MiniKit } from '@worldcoin/minikit-js';
import { isConfiguredWorldAppId, walletAppConfig } from './config';
import { supabase } from './supabaseClient';

const REQUEST_TIMEOUT_MS = 15_000;
const WALLET_AUTH_TIMEOUT_MS = 60_000;
const WALLET_AUTH_STATEMENT = 'Sign in to GGMOMOMO Games';

let activeWalletAuthPromise: Promise<unknown> | null = null;

export type WorldWalletAuthErrorCode =
  | 'auth_not_configured'
  | 'world_app_unavailable'
  | 'world_app_not_configured'
  | 'world_app_timeout'
  | 'world_app_network_failed'
  | 'world_app_nonce_failed'
  | 'world_app_rejected'
  | 'world_app_wallet_missing'
  | 'world_app_verification_failed'
  | 'world_app_session_failed'
  | 'world_app_sign_in_failed';

export class WorldWalletAuthError extends Error {
  constructor(public readonly code: WorldWalletAuthErrorCode) {
    super(code);
    this.name = 'WorldWalletAuthError';
  }
}

type NoncePayload = {
  nonce: string;
  requestId: string;
  statement: typeof WALLET_AUTH_STATEMENT;
  expiresAt: string;
};

function getFunctionBaseUrl(): string {
  return `${walletAppConfig.supabaseUrl.replace(/\/$/, '')}/functions/v1`;
}

async function fetchWithTimeout(input: RequestInfo | URL, init?: RequestInit): Promise<Response> {
  const controller = new AbortController();
  const timeoutId = window.setTimeout(() => controller.abort(), REQUEST_TIMEOUT_MS);

  try {
    return await fetch(input, { ...init, signal: controller.signal });
  } catch (error) {
    if (error instanceof DOMException && error.name === 'AbortError') {
      throw new WorldWalletAuthError('world_app_timeout');
    }
    throw new WorldWalletAuthError('world_app_network_failed');
  } finally {
    window.clearTimeout(timeoutId);
  }
}

async function readJson(response: Response, errorCode: WorldWalletAuthErrorCode): Promise<unknown> {
  try {
    return await response.json();
  } catch {
    throw new WorldWalletAuthError(errorCode);
  }
}

function isValidNoncePayload(payload: unknown): payload is NoncePayload {
  return (
    typeof payload === 'object' && payload !== null &&
    'nonce' in payload && typeof payload.nonce === 'string' && payload.nonce.length >= 16 &&
    'requestId' in payload && typeof payload.requestId === 'string' &&
    'statement' in payload && payload.statement === WALLET_AUTH_STATEMENT &&
    'expiresAt' in payload && typeof payload.expiresAt === 'string' &&
    !Number.isNaN(Date.parse(payload.expiresAt)) && Date.parse(payload.expiresAt) > Date.now()
  );
}

function getWalletAuthErrorCode(error: unknown): WorldWalletAuthErrorCode {
  if (
    typeof error === 'object' && error !== null &&
    (('error_code' in error && error.error_code === 'user_rejected') ||
      ('code' in error && error.code === 'user_rejected'))
  ) {
    return 'world_app_rejected';
  }
  return 'world_app_sign_in_failed';
}

async function requestWalletAuth(input: {
  nonce: string;
  requestId: string;
  statement: string;
  expiresAt: string;
}) {
  if (activeWalletAuthPromise) {
    throw new WorldWalletAuthError('world_app_timeout');
  }

  const commandPromise = MiniKit.walletAuth({
    nonce: input.nonce,
    requestId: input.requestId,
    statement: input.statement,
    expirationTime: new Date(input.expiresAt),
    notBefore: new Date(Date.now() - 60_000),
  }).then((result) => {
    if (result.executedWith !== 'minikit' || !result.data) {
      throw new WorldWalletAuthError('world_app_unavailable');
    }
    return result.data;
  });
  activeWalletAuthPromise = commandPromise;
  const clearActiveWalletAuth = () => {
    if (activeWalletAuthPromise === commandPromise) {
      activeWalletAuthPromise = null;
    }
  };
  commandPromise.then(clearActiveWalletAuth, clearActiveWalletAuth);

  let timeoutId: number | undefined;
  try {
    return await Promise.race([
      commandPromise,
      new Promise<never>((_, reject) => {
        timeoutId = window.setTimeout(() => reject(new WorldWalletAuthError('world_app_timeout')), WALLET_AUTH_TIMEOUT_MS);
      }),
    ]);
  } finally {
    if (timeoutId !== undefined) {
      window.clearTimeout(timeoutId);
    }
  }
}

export async function signInWithWorldWallet(): Promise<void> {
  if (!supabase) {
    throw new WorldWalletAuthError('auth_not_configured');
  }
  if (!MiniKit.isInstalled()) {
    throw new WorldWalletAuthError('world_app_unavailable');
  }
  if (!isConfiguredWorldAppId(walletAppConfig.appId)) {
    throw new WorldWalletAuthError('world_app_not_configured');
  }

  const nonceResponse = await fetchWithTimeout(`${getFunctionBaseUrl()}/generate-nonce`);
  if (!nonceResponse.ok) {
    throw new WorldWalletAuthError('world_app_nonce_failed');
  }

  const noncePayload = await readJson(nonceResponse, 'world_app_nonce_failed');
  if (!isValidNoncePayload(noncePayload)) {
    throw new WorldWalletAuthError('world_app_nonce_failed');
  }

  let walletAuthPayload;
  try {
    walletAuthPayload = await requestWalletAuth(noncePayload);
  } catch (error) {
    if (error instanceof WorldWalletAuthError) {
      throw error;
    }
    throw new WorldWalletAuthError(getWalletAuthErrorCode(error));
  }

  if (!walletAuthPayload.address || !walletAuthPayload.message || !walletAuthPayload.signature) {
    throw new WorldWalletAuthError('world_app_wallet_missing');
  }

  const verificationResponse = await fetchWithTimeout(`${getFunctionBaseUrl()}/verify-wallet-auth`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      payload: walletAuthPayload,
      nonce: noncePayload.nonce,
      requestId: noncePayload.requestId,
      statement: noncePayload.statement,
      walletAddress: walletAuthPayload.address,
      username: MiniKit.user?.username ?? null,
    }),
  });
  if (!verificationResponse.ok) {
    throw new WorldWalletAuthError('world_app_verification_failed');
  }

  const verification = await readJson(verificationResponse, 'world_app_verification_failed');
  if (
    typeof verification !== 'object' || verification === null ||
    !('success' in verification) || verification.success !== true ||
    !('tokenHash' in verification) || typeof verification.tokenHash !== 'string' || !verification.tokenHash
  ) {
    throw new WorldWalletAuthError('world_app_verification_failed');
  }

  const { error } = await supabase.auth.verifyOtp({ token_hash: verification.tokenHash, type: 'magiclink' });
  if (error) {
    throw new WorldWalletAuthError('world_app_session_failed');
  }
}
